import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Flame, Star, Trophy, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StreakCounterProps {
  streak: {
    current: number;
    longest: number;
    last_completed_date?: string;
  };
}

export function StreakCounter({ streak }: StreakCounterProps) {
  const { current, longest } = streak;

  // Get milestone info and visual elements
  const getMilestone = () => {
    if (current >= 100) {
      return {
        icon: Trophy,
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
        borderColor: 'border-yellow-200 dark:border-yellow-800',
        label: 'Leggenda',
        message: 'Incredibile! 100+ giorni consecutivi!',
      };
    }
    if (current >= 30) {
      return {
        icon: Star,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100 dark:bg-purple-900/20',
        borderColor: 'border-purple-200 dark:border-purple-800',
        label: 'Campione',
        message: 'Fantastico! Un mese intero!',
      };
    }
    if (current >= 7) {
      return {
        icon: Zap,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100 dark:bg-blue-900/20',
        borderColor: 'border-blue-200 dark:border-blue-800',
        label: 'In fiamme',
        message: 'Una settimana di costanza!',
      };
    }
    return {
      icon: Flame,
      color: 'text-orange-500',
      bgColor: 'bg-orange-100 dark:bg-orange-900/20',
      borderColor: 'border-orange-200 dark:border-orange-800',
      label: 'Iniziato',
      message: 'Continua così!',
    };
  };

  const milestone = getMilestone();
  const MilestoneIcon = milestone.icon;

  // Calculate progress to next milestone
  const getNextMilestone = () => {
    if (current < 7) return { target: 7, label: 'una settimana' };
    if (current < 30) return { target: 30, label: 'un mese' };
    if (current < 100) return { target: 100, label: '100 giorni' };
    return null;
  };

  const nextMilestone = getNextMilestone();
  const progress = nextMilestone ? (current / nextMilestone.target) * 100 : 100;

  return (
    <Card className={cn('border-0 shadow-sm', milestone.bgColor, milestone.borderColor, 'border')}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <div className="flex items-center gap-2">
            <MilestoneIcon className={cn('h-6 w-6', milestone.color)} />
            Serie giorni
          </div>
          {milestone.label !== 'Iniziato' && (
            <Badge variant="secondary" className={milestone.color}>
              {milestone.label}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Streak - Big Number */}
        <div className="text-center">
          <div className={cn('text-4xl font-bold', milestone.color)}>{current}</div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">giorni consecutivi</p>
        </div>

        {/* Milestone Message */}
        <div className="text-center">
          <p className={cn('text-sm font-medium', milestone.color)}>{milestone.message}</p>
        </div>

        {/* Progress to Next Milestone */}
        {nextMilestone && (
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
              <span>Prossimo traguardo:</span>
              <span>{nextMilestone.target - current} giorni</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
              <div
                className={cn('h-2 rounded-full transition-all duration-300', {
                  'bg-orange-500': current < 7,
                  'bg-blue-600': current >= 7 && current < 30,
                  'bg-purple-600': current >= 30 && current < 100,
                })}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Longest Streak */}
        {longest > current && (
          <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Record personale:</span>
              <span className="font-medium text-gray-900 dark:text-white">{longest} giorni</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
